import { 
  Box, 
  Button, 
  Flex, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  useColorModeValue,
  SimpleGrid,
  Select,
  Input,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import LeaveTable from "../../components/tables/LeaveTable";
import { useGetLeave } from "../../services/employeeService";
import { FiCalendar, FiClock, FiCheckCircle, FiXCircle, FiFilter, FiSearch } from "react-icons/fi";

const Leave = () => {
  const { leaveData, leaveIsLoading, refetchLeave, leaveSetFilter } = useGetLeave({ enabled: true });
  const [filterData, setFilterData] = useState({
    status: "",
    search: "",
    startDate: "",
    endDate: ""
  });
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Calculate leave statistics
  const leaveStats = useMemo(() => {
    if (!leaveData?.length) return { total: 0, pending: 0, approved: 0, rejected: 0 };
    
    return leaveData.reduce((stats, leave) => {
      stats.total++;
      if (leave.status === "Pending") stats.pending++;
      else if (leave.status === "Approved") stats.approved++;
      else if (leave.status === "Rejected") stats.rejected++;
      return stats;
    }, { total: 0, pending: 0, approved: 0, rejected: 0 });
  }, [leaveData]);

  // Sort leave data in descending order by date
  const sortedLeaveData = useMemo(() => {
    if (!leaveData?.length) return [];
    
    return [...leaveData].sort((a, b) => {
      const dateA = new Date(a.leave_start);
      const dateB = new Date(b.leave_start);
      return dateB - dateA; // Descending order (newest first)
    });
  }, [leaveData]);

  const handleFilter = () => {
    const payload = {
      status: filterData.status || undefined,
      search: filterData.search || undefined,
      start_date: filterData.startDate || undefined,
      end_date: filterData.endDate || undefined
    };
    leaveSetFilter(payload);
  };

  const handleClearFilters = () => {
    setFilterData({
      status: "",
      search: "",
      startDate: "",
      endDate: ""
    });
    leaveSetFilter({});
  };

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch" mb={8}>
        <Heading size="lg" color="gray.900" fontWeight="600">
          Leave Management
        </Heading>
        <Text color="gray.600" fontSize="md">
          Review and manage employee leave requests
        </Text>
      </VStack>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <StatCard
          label="Total Requests"
          value={leaveStats.total}
          icon={<FiCalendar />}
          color="blue"
        />
        <StatCard
          label="Pending"
          value={leaveStats.pending}
          icon={<FiClock />}
          color="orange"
        />
        <StatCard
          label="Approved"
          value={leaveStats.approved}
          icon={<FiCheckCircle />}
          color="green"
        />
        <StatCard
          label="Rejected"
          value={leaveStats.rejected}
          icon={<FiXCircle />}
          color="red"
        />
      </SimpleGrid>

      {/* Search and Filters */}
      <Box
        bg={bgColor}
        borderRadius="16px"
        p={6}
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        mb={6}
      >
        <VStack spacing={6} align="stretch">
          <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
            <VStack align="start" spacing={2}>
              <Heading size="md" color="gray.900" fontWeight="600">
                Leave Requests
              </Heading>
              <Text color="gray.600" fontSize="sm">
                {leaveStats.total} total requests • {leaveStats.pending} pending approval
              </Text>
            </VStack>
            <HStack spacing={3}>
              <Button
                leftIcon={<FiFilter />}
                colorScheme="blue"
                variant="outline"
                onClick={handleFilter}
                size="md"
                borderRadius="12px"
              >
                Apply Filters
              </Button>
              <Button
                colorScheme="gray"
                variant="outline"
                onClick={handleClearFilters}
                size="md"
                borderRadius="12px"
              >
                Clear Filters
              </Button>
            </HStack>
          </Flex>

          <HStack spacing={4} align="center" flexWrap="wrap">
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Search Employee:
              </Text>
              <Input
                placeholder="Search by name or ID..."
                value={filterData.search}
                onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                size="md"
                borderRadius="12px"
                w="200px"
                leftIcon={<FiSearch />}
              />
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Status:
              </Text>
              <Select
                placeholder="All Status"
                value={filterData.status}
                onChange={(e) => setFilterData({ ...filterData, status: e.target.value })}
                size="md"
                borderRadius="12px"
                w="150px"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </Select>
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Start Date:
              </Text>
              <Input
                type="date"
                value={filterData.startDate}
                onChange={(e) => setFilterData({ ...filterData, startDate: e.target.value })}
                size="md"
                borderRadius="12px"
                w="150px"
              />
            </VStack>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                End Date:
              </Text>
              <Input
                type="date"
                value={filterData.endDate}
                onChange={(e) => setFilterData({ ...filterData, endDate: e.target.value })}
                size="md"
                borderRadius="12px"
                w="150px"
              />
            </VStack>
          </HStack>
        </VStack>
      </Box>

      {/* Leave Table */}
      <Box
        bg={bgColor}
        borderRadius="16px"
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <LeaveTable 
          loading={leaveIsLoading} 
          employeeData={sortedLeaveData} 
          refetchLeave={refetchLeave} 
        />
      </Box>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", iconBg: "blue.100" },
    green: { bg: "green.50", color: "green.600", iconBg: "green.100" },
    red: { bg: "red.50", color: "red.600", iconBg: "red.100" },
    orange: { bg: "orange.50", color: "orange.600", iconBg: "orange.100" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.100"
      _hover={{ transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg={colors.iconBg}
          color={colors.color}
        >
          {icon}
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {label}
          </Text>
          <Text fontSize="2xl" fontWeight="700" color="gray.900">
            {value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default Leave;
